// Carrier Recovery (Sahin 2026-10-22): one page for getting money back from
// carriers, replacing the separate Disputes and Carrier Claims pages.
//
//   Disputes   - a carrier charge we think is wrong (the Disputes page as it was)
//   Claims     - lost / damaged parcels: claims list + carrier credits
//   To review  - overcharges no rebill or dispute has caught yet (was "Suspicious")
//   Settings   - carrier dispute contacts / e-mail templates + overcharge check rules
//
// The tab is in the URL (?tab=disputes|claims|review|settings), so the old
// /disputes and /claims links land on the right tab.

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { useAuth } from '../lib/auth'
import { useCurrency } from '../lib/currency-context'
import { fmtMoney } from '../lib/money'
import { DisputesView, NewDisputeModal, type DisputePrefill } from './Disputes'
import { ClaimsView, ToReviewTab, RulesTab } from './Claims'
import { DisputeContacts, DisputeRulesEditor } from './Settings'
import { CarrierRepliesTab } from '../components/CarrierReplies'
import { CarrierPaymentsTab } from '../components/CarrierPayments'

type Tab = 'disputes' | 'claims' | 'payments' | 'replies' | 'review' | 'settings'

interface DisputeSummary {
  kpis: { open: { n: number; total_minor: number }; won: { n: number; total_minor: number }; credited: { n: number; total_minor: number }; lost: { n: number; total_minor: number } }
  lifecycle: { stage: string; n: number }[]
}
interface ClaimKpis { total_owed_to_customers_minor: number; carrier_paid_not_credited: number }

export function CarrierRecoveryView() {
  const { can } = useAuth()
  const { currency } = useCurrency()
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const [disputePrefill, setDisputePrefill] = useState<DisputePrefill | null>(null)

  const tabs: { key: Tab; label: string; show: boolean }[] = [
    { key: 'disputes', label: 'Disputes',  show: can('disputes') },
    { key: 'claims',   label: 'Claims',    show: can('claims') },
    { key: 'payments', label: 'Payments',  show: can('claims') || can('disputes') },
    { key: 'replies',  label: 'Carrier replies', show: can('disputes') },
    { key: 'review',   label: 'To review', show: can('disputes') },
    { key: 'settings', label: 'Settings',  show: can('disputes') },
  ]
  const visible = tabs.filter(t => t.show)
  const asked = params.get('tab') as Tab | null
  const tab: Tab = visible.find(t => t.key === asked)?.key ?? visible[0]?.key ?? 'disputes'
  const setTab = (t: Tab) => setParams({ tab: t }, { replace: true })

  const disputes = useQuery<DisputeSummary>({
    queryKey: ['disputes-summary'],
    queryFn: () => fetch('/api/disputes/summary').then(r => r.json()),
    enabled: can('disputes'),
  })
  const claims = useQuery<ClaimKpis>({
    queryKey: ['claims', 'kpis'],
    queryFn: () => fetch('/api/claims/kpis').then(r => r.json()),
    enabled: can('claims'),
  })
  const k = disputes.data?.kpis
  const submitted = disputes.data?.lifecycle.find(l => l.stage === 'Submitted')?.n

  if (!visible.length) {
    return <Card className="p-6 text-[13px] text-ink-500">You do not have access to disputes or claims.</Card>
  }

  return (
    <div className="space-y-5">
      <div>
        <div className="text-[12px] font-semibold text-flame-600 uppercase tracking-wider">Carrier-side</div>
        <h1 className="text-[28px] font-bold tracking-tight mt-1">Carrier Recovery</h1>
        <p className="text-ink-500 text-[13px] mt-1">
          Money back from carriers: wrong charges we dispute, and lost or damaged parcels the carrier pays for.
        </p>
      </div>

      {/* totals across both */}
      <Card className="flex flex-wrap divide-x divide-ink-100">
        {can('disputes') && (
          <Stat label="Open disputes" value={String(k?.open.n ?? '—')}
            sub={k ? `${fmtMoney(k.open.total_minor, currency)} at stake` : ''} tone="text-flame-600" />
        )}
        {can('disputes') && (
          <Stat label="Waiting on carrier" value={submitted != null ? String(submitted) : '—'} sub="disputes submitted" tone="text-royal-600" />
        )}
        {can('claims') && (
          <Stat label="Owed to customers" value={claims.data ? fmtMoney(claims.data.total_owed_to_customers_minor, currency) : '—'}
            sub={claims.data ? `${claims.data.carrier_paid_not_credited} claim(s) to credit` : ''} tone="text-honey-600" />
        )}
        {can('disputes') && (
          <Stat label="Recovered (all time)"
            value={k ? fmtMoney(k.won.total_minor + k.credited.total_minor, currency) : '—'}
            sub="disputes waived or credited" tone="text-mint-600" />
        )}
        {can('disputes') && (
          <Stat label="Win rate"
            value={k && (k.won.n + k.lost.n) > 0 ? `${Math.round(k.won.n / (k.won.n + k.lost.n) * 100)}%` : '—'}
            sub="waived ÷ decided by carriers" tone="text-ink-900" />
        )}
      </Card>

      <div className="flex gap-1 border-b border-ink-200">
        {visible.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key ? 'border-ink-900 text-ink-900' : 'border-transparent text-ink-500 hover:text-ink-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'disputes' && <DisputesView embedded />}
      {tab === 'claims' && <ClaimsView embedded />}
      {tab === 'payments' && <CarrierPaymentsTab />}
      {tab === 'replies' && <CarrierRepliesTab />}
      {tab === 'review' && (
        <ToReviewTab canDispute={can('resolve_disputes')}
          onDispute={r => setDisputePrefill({
            carrier: r.carrier.toUpperCase(),
            invoice: r.invoice_number ?? '',
            shipment: r.tracking_number,
            amount: r.delta_minor != null ? (r.delta_minor / 100).toFixed(2) : '',
            chargeName: r.charge_description ?? 'Charge above the label price',
          })} />
      )}
      {tab === 'settings' && (
        <div className="space-y-5">
          <DisputeContacts />
          <DisputeRulesEditor />
          <div>
            <div className="text-[15px] font-semibold text-ink-900 mb-1">Overcharge check ("To review")</div>
            <p className="text-[12.5px] text-ink-500 mb-3">When a carrier charge is listed for review, per carrier, and which charges are always flagged.</p>
            <RulesTab />
          </div>
        </div>
      )}

      {disputePrefill && (
        <NewDisputeModal prefill={disputePrefill} onClose={() => setDisputePrefill(null)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['detection', 'suspicious'] })} />
      )}
    </div>
  )
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone: string }) {
  return (
    <div className="flex-1 min-w-[180px] px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">{label}</div>
      <div className={`text-[22px] font-bold mt-0.5 ${tone}`}>{value}</div>
      {sub && <div className="text-[11.5px] text-ink-500 mt-0.5">{sub}</div>}
    </div>
  )
}
